import { KeyCodes } from "@/constants/KeyCodes"
import { IOption } from "@/types"
import classNames from "classnames"
import { clsx } from "clsx"
import { CSSProperties, ReactNode, RefObject, useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import "./style.css"

export interface IBSelectProps<T extends string> {
  label?: string
  placeholder?: string
  errorText?: string
  value?: IOption<T> | T | null
  options: IOption<T>[]
  isDark?: boolean
  fullWidth?: boolean
  onSelect?: (value: IOption | null) => void
  size?: "small" | "large"
  getOptionLabel?: (value: IOption<T>) => T
  leftIcon?: ReactNode
  renderOption?: (option: IOption<T>, index: number) => ReactNode
  searchable?: boolean
  buttonStyle?: CSSProperties
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  menuStyle?: CSSProperties
  noOptionText?: string
  centered?: boolean
  isError?: boolean
  renderLabel?: (option: IOption<T>) => ReactNode
  listRef?: RefObject<HTMLUListElement>
  onOpen?: () => void
  onClose?: () => void
  onLoadBtnRef?: (btnRef: RefObject<HTMLButtonElement>) => void
}

const DropdownArrow = () => {
  return (
    <svg
      className="dropdown-icon"
      width="7"
      height="8"
      viewBox="0 0 7 8"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1.34938 3.70133L5.49909 1.3055C5.13123 2.82969 5.10248 4.41321 5.41065 5.94398C4.23904 4.91172 2.8533 4.14485 1.34938 3.70133Z" />
    </svg>
  )
}
export const BSelect = <T extends string>({
  isDark,
  value,
  searchable,
  buttonStyle,
  menuStyle,
  size,
  noOptionText,
  inputProps,
  label,
  renderLabel,
  onOpen,
  onClose,
  isError = false,
  onLoadBtnRef,
  ...props
}: IBSelectProps<T>) => {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdown = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [selected, setSelected] = useState<IOption<T> | null>(null)
  const [inputValue, setInputValue] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [filteredOptions, setFilteredOptions] = useState<typeof options>([])
  const { options, onSelect, renderOption, getOptionLabel, centered = false, ...rest } = props

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    if (typeof value === "object") {
      setSelected(value as IOption<T> | null)
      if (input.current != null) input.current.value = value?.label ?? selected?.label ?? ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, input])

  const onShowDropdown = (e: Event) => {
    onOpen?.()
  }
  const onCloseDropdown = (e: Event) => {
    onClose?.()
  }
  useEffect(() => {
    const current = dropdown.current
    if (current != null) {
      current.addEventListener("shown.bs.dropdown", onShowDropdown)
      current.addEventListener("hide.bs.dropdown", onCloseDropdown)
    }
    return () => {
      current?.removeEventListener("shown.bs.dropdown", onShowDropdown)

      current.removeEventListener("hide.bs.dropdown", onCloseDropdown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown])
  useEffect(() => {
    if (listRef.current) {
      const dropdownNode = listRef.current as HTMLUListElement
      const highlightedItem = dropdownNode.querySelector(".selected-with-arrow") as HTMLDivElement
      if (highlightedItem) {
        const isBigger = highlightedItem.offsetTop > dropdownNode.clientHeight
        dropdownNode.scrollTop = isBigger
          ? highlightedItem.offsetTop - (dropdownNode.clientHeight - highlightedItem.clientHeight)
          : 0
      }
    }
  }, [selectedIndex])
  useEffect(() => {
    if (btnRef.current != null) onLoadBtnRef?.(btnRef)
  }, [btnRef, onLoadBtnRef])
  useEffect(() => {
    if (typeof value === "string") {
      setSelected(options.find((option) => option.value === value))
      setInputValue(value)
    } else setSelected(value)
  }, [value, options])
  return (
    <div style={{ position: "relative", width: rest.fullWidth ? "100%" : "auto" }}>
      {label != null && (
        <span className={`${props.errorText != null ? "bs-error-label" : ""}`}>{label}</span>
      )}
      <div
        ref={dropdown}
        className={classNames("dropdown b-select-wrapper", {
          centered: centered,
          dropdownLight: !isDark,
        })}
        onClick={() => {
          inputRef.current?.focus()
        }}
      >
        <button
          ref={btnRef}
          className={classNames("b-select", {
            BSelectLarge: size === "large",
            error_input: isError,
          })}
          type="button"
          id="dropdownMenuButton1"
          data-bs-toggle="dropdown"
          style={buttonStyle}
        >
          {props.leftIcon}
          {searchable ? (
            <input
              ref={inputRef}
              type="text"
              style={{ pointerEvents: "initial" }}
              placeholder={intl.formatMessage({ id: "select" })}
              value={inputValue}
              onKeyDown={(e) => {
                const isOpened = btnRef.current?.classList.contains("show") ?? false
                if (e.key === KeyCodes.ArrowDown || e.key === KeyCodes.ArrowUp) {
                  e.preventDefault()
                  const isDown = e.key === KeyCodes.ArrowDown
                  setSelectedIndex((old) =>
                    isDown ? Math.min(old + 1, filteredOptions.length - 1) : Math.max(old - 1, 0),
                  )
                  if (!isOpened) btnRef.current?.click()
                }
                if (e.key === KeyCodes.Enter) {
                  e.preventDefault()
                  setInputValue(filteredOptions[selectedIndex].label)
                  onSelect?.(filteredOptions[selectedIndex])
                  document.dispatchEvent(new Event("click"))
                }
              }}
              onInput={(e) => {
                const inputValue = (e.target as HTMLInputElement).value
                setInputValue(inputValue)
                setSelectedIndex(-1)
                if (!btnRef.current?.classList.contains("show")) btnRef.current?.click()
                if (inputValue.length === 0) setFilteredOptions(options)
                else
                  setFilteredOptions(
                    options.filter((option) =>
                      option.label.toLowerCase().startsWith(inputValue.toLowerCase().trim()),
                    ),
                  )
              }}
            />
          ) : (
            <>
              {renderLabel?.(selected) ?? getOptionLabel?.(selected) ?? (
                <span>{selected?.label}</span>
              )}
            </>
          )}
          <DropdownArrow />
        </button>
        <div className="connector"></div>
        <div
          style={menuStyle}
          ref={menu}
          className="dropdown-menu b-select-menu"
          aria-labelledby="dropdownMenuButton1"
        >
          {filteredOptions.length === 0 && (
            <span className="no-option">{noOptionText ?? "No options"}</span>
          )}
          <ul ref={listRef}>
            {filteredOptions.map((option, ind) => (
              <li
                key={ind}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (onSelect != null) onSelect(option)
                  setSelected(option as IOption<T>)
                  setInputValue(option.label)
                  if (input.current != null) input.current.value = option.label
                }}
              >
                <div
                  className={clsx("dropdown-item", {
                    "selected-with-arrow": ind === selectedIndex,
                  })}
                >
                  {renderOption != null ? (
                    renderOption(option as IOption<T>, ind)
                  ) : (
                    <span>{option.label}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {rest.errorText && <span className="bs-error-label">{rest.errorText}</span>}
    </div>
  )
}
